<template>
  <Teleport to="body">
    <div
      v-if="modelValue"
      class="activity-detail-floating-title-wrap"
      aria-hidden="true"
    >
      <h2 class="activity-detail-floating-title">
        {{ floatingTitleText }}
      </h2>
    </div>
  </Teleport>
  <el-dialog
    :model-value="modelValue"
    class="activity-detail-dialog"
    modal-class="activity-detail-dialog-mask"
    :show-close="true"
    destroy-on-close
    width="min(1000px, 94vw)"
    append-to-body
    @update:model-value="$emit('update:modelValue', $event)"
  >
    <template #header>
      <span class="activity-dlg-header-sr" />
    </template>
    <div v-loading="loading" class="detail-body">
      <template v-if="detail">
        <div class="detail-top">
          <div class="detail-media">
            <div class="hero">
              <img
                v-if="detail.coverUrl"
                class="cover"
                :src="detail.coverUrl"
                alt=""
              />
              <div v-else class="cover cover--ph" />
              <span class="badge" :class="'badge--' + badgeKey">{{
                badgeLabel
              }}</span>
            </div>
          </div>
          <div class="detail-info">
            <div class="field-block">
              <div class="field-label">发起人</div>
              <div
                v-if="detail.organizerName || detail.organizerAvatarUrl"
                class="organizer-row"
              >
                <img
                  v-if="detail.organizerAvatarUrl"
                  class="org-avatar"
                  :src="detail.organizerAvatarUrl"
                  alt=""
                />
                <div v-else class="org-avatar org-avatar--ph">
                  {{ (detail.organizerName || "?").slice(0, 1) }}
                </div>
                <span class="organizer-name">{{
                  detail.organizerName || "—"
                }}</span>
              </div>
              <div v-else class="field-muted">—</div>
            </div>
            <div class="field-block">
              <div class="field-label">活动简介</div>
              <p class="desc">{{ detail.description || "暂无简介" }}</p>
            </div>
            <div class="time-block">
              <div class="field-label">活动时间</div>
              <div class="time-lines">
                <div class="time-line">
                  <span class="time-k">开始时间</span>
                  <span class="time-v">{{ fmt(detail.startAt) }}</span>
                </div>
                <div class="time-line">
                  <span class="time-k">结束时间</span>
                  <span class="time-v">{{ fmt(detail.endAt) }}</span>
                </div>
              </div>
              <div class="deadline-actions-row">
                <div class="time-line deadline-line">
                  <span class="time-k">截止时间</span>
                  <span class="time-v">{{ fmt(detail.signupDeadline) }}</span>
                </div>
                <div class="deadline-actions-right">
                  <span class="quota-inline">
                    <span :class="enrollClass">{{
                      detail.enrolledCount ?? 0
                    }}</span>
                    <span class="quota-sep"> / </span>
                    <span class="quota-max">{{
                      detail.maxParticipants || "—"
                    }}</span>
                  </span>
                  <div class="action-btns">
                    <el-button
                      v-if="!isRegistered && canSignup"
                      type="primary"
                      :loading="actionLoading"
                      @click="onSignup"
                    >
                      报名
                    </el-button>
                    <template
                      v-else-if="isRegistered && canEnter && detail.linkUrl"
                    >
                      <el-button type="info" disabled>已报名</el-button>
                      <el-button type="success" @click="onEnter">
                        立即进入
                      </el-button>
                    </template>
                    <el-button
                      v-else-if="isRegistered"
                      type="info"
                      disabled
                      :title="enterHint || undefined"
                    >
                      已报名
                    </el-button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="msg-section">
          <div class="msg-head">活动留言</div>
          <div class="msg-list">
            <div
              v-for="m in detail.messages || []"
              :key="m.id"
              class="msg-item"
            >
              <img
                class="msg-av"
                :src="m.userAvatarUrl || defaultAvatar"
                alt=""
              />
              <div class="msg-main">
                <div class="msg-top">
                  <span class="msg-name">{{ m.userName }}</span>
                  <span class="msg-time">{{ fmt(m.createdAt) }}</span>
                </div>
                <div class="msg-content">{{ m.content }}</div>
              </div>
            </div>
            <div v-if="!detail.messages?.length" class="msg-empty">
              暂无留言
            </div>
          </div>
          <div v-if="hasLogin" class="msg-form">
            <el-input
              v-model="msgText"
              type="textarea"
              :rows="2"
              maxlength="500"
              show-word-limit
              placeholder="写下留言…"
            />
            <el-button
              type="primary"
              class="msg-send"
              :loading="msgLoading"
              @click="onSendMessage"
            >
              发表留言
            </el-button>
          </div>
          <div v-else class="msg-login-tip">登录后可参与留言</div>
        </div>
      </template>
    </div>
  </el-dialog>
</template>

<script setup>
/* eslint-disable no-undef -- defineProps / defineEmits 为 Vue 编译宏 */
import { ref, computed, watch } from "vue";
import { useToast } from "vue-toast-notification";
import { useUserStore } from "@/stores/user";
import { getToken } from "@/utils/cookie.js";
import {
  getActivityDetail,
  signupActivity,
  postActivityMessage,
} from "@/api/space";
import defaultAvatar from "@/assets/images/avatar.jpg";
import {
  getActivityBadgeKey,
  ACTIVITY_BADGE_LABEL,
  canSignupClient,
  canEnterActivity,
  getEnrollmentRatioClass,
} from "@/utils/activityUi";

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  activityId: { type: [Number, String], default: null },
});

const emit = defineEmits(["update:modelValue", "signup-success"]);

const $toast = useToast();
const userStore = useUserStore();
/** 与请求拦截器一致：以 Token 为准；避免 getUser 未完成时 userInfo.id 仍为 0 误判未登录 */
const hasLogin = computed(() => Boolean(userStore.token || getToken()));

/** 详情接口在匿名 GET 时可能无 registered；与模板统一用此判断 */
const isRegistered = computed(() => {
  const r = detail.value?.registered;
  return r === true || r === 1 || r === "true";
});

const loading = ref(false);
const detail = ref(null);
const actionLoading = ref(false);
const msgLoading = ref(false);
const msgText = ref("");

const badgeKey = computed(() =>
  detail.value ? getActivityBadgeKey(detail.value) : "NOT_STARTED"
);
const badgeLabel = computed(() => ACTIVITY_BADGE_LABEL[badgeKey.value] || "");

/** 弹窗上方悬浮标题（框外） */
const floatingTitleText = computed(() => {
  if (!props.modelValue) return "";
  if (detail.value?.title) return detail.value.title;
  if (loading.value) return "加载中…";
  return "";
});

const canSignup = computed(() =>
  detail.value ? canSignupClient(detail.value) : false
);
const canEnter = computed(() =>
  detail.value && isRegistered.value ? canEnterActivity(detail.value) : false
);

const enrollClass = computed(() =>
  getEnrollmentRatioClass(
    detail.value?.enrolledCount,
    detail.value?.maxParticipants
  )
);

const enterHint = computed(() => {
  if (!isRegistered.value) return "";
  const t = Date.now();
  const start = detail.value.startAt
    ? new Date(detail.value.startAt).getTime()
    : null;
  if (start != null && !Number.isNaN(start) && t < start) return "活动尚未开始";
  return "活动已结束或入口未配置";
});

function fmt(v) {
  if (!v) return "—";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v);
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(
    d.getHours()
  )}:${p(d.getMinutes())}`;
}

async function load() {
  if (!props.activityId) return;
  loading.value = true;
  try {
    const res = await getActivityDetail(props.activityId);
    if (res?.code === 200) detail.value = res.data;
    else {
      detail.value = null;
      $toast.error(res?.message || "加载失败", { position: "top" });
    }
  } catch {
    detail.value = null;
    $toast.error("加载失败", { position: "top" });
  } finally {
    loading.value = false;
  }
}

watch(
  () => [props.modelValue, props.activityId],
  ([open]) => {
    if (open && props.activityId) {
      msgText.value = "";
      load();
    }
    if (!open) detail.value = null;
  }
);

async function onSignup() {
  if (!hasLogin.value) {
    $toast.warning("请先登录", { position: "top" });
    return;
  }
  actionLoading.value = true;
  try {
    const res = await signupActivity(props.activityId);
    if (res?.code === 200) {
      const d = res.data && typeof res.data === "object" ? res.data : {};
      detail.value = { ...d, registered: true };
      emit("signup-success", { ...d, registered: true });
      $toast.success("报名成功", { position: "top" });
    } else {
      $toast.error(res?.message || "报名失败", { position: "top" });
    }
  } catch {
    $toast.error("报名失败", { position: "top" });
  } finally {
    actionLoading.value = false;
  }
}

function onEnter() {
  const url = detail.value?.linkUrl || "";
  if (url.startsWith("http")) window.open(url, "_blank");
  else if (url) {
    const base = `${window.location.origin}${(
      process.env.BASE_URL || "/"
    ).replace(/\/$/, "")}`;
    window.open(`${base}/space/view/${encodeURIComponent(url)}`, "_blank");
  } else {
    $toast.info("未配置进入链接", { position: "top" });
  }
}

async function onSendMessage() {
  if (!hasLogin.value) return;
  const t = msgText.value.trim();
  if (!t) {
    $toast.warning("请输入留言", { position: "top" });
    return;
  }
  msgLoading.value = true;
  try {
    const res = await postActivityMessage(props.activityId, {
      content: t,
      userName: userStore.userInfo?.username || "",
      userAvatarUrl: userStore.userInfo?.icon || "",
    });
    if (res?.code === 200) {
      detail.value = res.data;
      msgText.value = "";
      $toast.success("已发布", { position: "top" });
    } else {
      $toast.error(res?.message || "发送失败", { position: "top" });
    }
  } catch {
    $toast.error("发送失败", { position: "top" });
  } finally {
    msgLoading.value = false;
  }
}
</script>

<style scoped>
/* 与 AppHeader 高度 86px 对齐：标题在导航下方，上下留白略大 */
.activity-detail-floating-title-wrap {
  position: fixed;
  left: 50%;
  transform: translateX(-50%);
  top: calc(86px + 24px);
  z-index: 10052;
  width: min(1000px, 94vw);
  max-width: calc(100vw - 24px);
  padding: 18px 48px 22px 24px;
  box-sizing: border-box;
  text-align: center;
  pointer-events: none;
}

.activity-detail-floating-title {
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.96);
  text-shadow: 0 1px 14px rgba(0, 0, 0, 0.7);
  line-height: 1.35;
  word-break: break-word;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  overflow: hidden;
}

.activity-dlg-header-sr {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

.detail-body {
  min-height: 120px;
}

.field-block {
  margin-bottom: 16px;
}

.field-label {
  font-size: 14px;
  font-weight: 600;
  color: var(--ux-text-muted);
  margin-bottom: 12px;
  letter-spacing: 0.02em;
}

.field-muted {
  font-size: 16px;
  color: var(--ux-text-muted);
}

.time-block {
  margin-top: 4px;
}

.time-lines {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.deadline-actions-row {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 12px 20px;
  flex-wrap: wrap;
  margin-top: 8px;
}

.deadline-line {
  flex: 1 1 auto;
  min-width: 0;
  margin: 0;
}

.deadline-actions-right {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 14px 16px;
  flex-shrink: 0;
}

.time-line {
  font-size: 15px;
  color: var(--ux-text-secondary);
  line-height: 1.5;
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 6px 10px;
}

.time-k {
  flex: 0 0 auto;
  min-width: 4.5em;
  color: var(--ux-text-muted);
}

.time-v {
  color: var(--ux-text-secondary);
}

.quota-inline {
  font-size: 15px;
  font-weight: 600;
  line-height: 1.2;
  white-space: nowrap;
  flex-shrink: 0;
}

.quota-sep,
.quota-max {
  color: var(--ux-text-secondary);
}

.action-btns {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 8px;
}

.detail-top {
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 20px;
  margin-bottom: 8px;
}

.detail-media {
  flex: 0 1 44%;
  max-width: 440px;
  min-width: 200px;
}

.detail-info {
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.hero {
  position: relative;
  border-radius: 14px;
  overflow: hidden;
  height: auto;
  min-height: 0;
  background: var(--ux-img-placeholder);
  display: flex;
  align-items: flex-start;
  justify-content: flex-start;
}

.cover {
  width: 100%;
  height: auto;
  max-height: min(52vh, 460px);
  object-fit: contain;
  object-position: left top;
  display: block;
}

.cover--ph {
  width: 100%;
  min-height: 200px;
}

.badge {
  position: absolute;
  left: 10px;
  top: 10px;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 600;
  backdrop-filter: blur(8px);
  background: rgba(0, 0, 0, 0.45);
  color: #9ca3af;
}

.badge--IN_PROGRESS {
  color: #ef4444;
}

.organizer-row {
  display: flex;
  align-items: center;
  gap: 10px;
}

.org-avatar {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
}

.org-avatar--ph {
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.08);
  font-size: 17px;
  font-weight: 600;
  color: var(--ux-text-muted);
}

.organizer-name {
  font-size: 17px;
  font-weight: 500;
  color: var(--ux-text-secondary);
}

.ratio--low {
  color: #4ade80;
  font-weight: 600;
}

.ratio--high {
  color: #f87171;
  font-weight: 600;
}

.ratio--neutral {
  color: var(--ux-text-secondary);
}

.desc {
  font-size: 16px;
  line-height: 1.65;
  color: var(--ux-text-secondary);
  margin: 0;
  white-space: pre-wrap;
}

.msg-section {
  border-top: 1px solid rgba(255, 255, 255, 0.12);
  padding-top: 20px;
  margin-top: 20px;
  min-height: 200px;
}

@media (max-width: 720px) {
  .detail-top {
    flex-direction: column;
  }

  .detail-media {
    max-width: none;
    flex: none;
    width: 100%;
    min-width: 0;
  }

  .cover--ph {
    min-height: 160px;
  }

  .cover {
    max-height: 44vh;
  }

  .deadline-actions-row {
    flex-direction: column;
    align-items: stretch;
  }

  .deadline-actions-right {
    width: 100%;
    justify-content: space-between;
  }
}
.msg-head {
  font-weight: 600;
  font-size: 17px;
  margin-bottom: 14px;
  color: var(--ux-text-primary);
}
.msg-list {
  max-height: min(38vh, 360px);
  overflow-y: auto;
  margin-bottom: 16px;
}
.msg-item {
  display: flex;
  gap: 10px;
  padding: 10px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}
.msg-av {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
}
.msg-top {
  display: flex;
  align-items: baseline;
  gap: 10px;
  margin-bottom: 4px;
}
.msg-name {
  font-size: 15px;
  font-weight: 600;
  color: var(--ux-text-primary);
}
.msg-time {
  font-size: 13px;
  color: var(--ux-text-muted);
}
.msg-content {
  font-size: 15px;
  color: var(--ux-text-secondary);
  line-height: 1.55;
}
.msg-empty {
  font-size: 15px;
  color: var(--ux-text-muted);
  padding: 12px 0;
}
.msg-form {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.msg-send {
  align-self: flex-end;
}
.msg-login-tip {
  font-size: 15px;
  color: var(--ux-text-muted);
}
</style>

<style>
/*
 * 活动详情弹窗 Teleport 到 body，scoped 的 data-v 不会打在 .el-dialog 上，
 * 之前用 :deep(.el-dialog.activity-detail-dialog) 往往选不中。
 * 这里用 modal-class「activity-detail-dialog-mask」作唯一锚点（无 scoped）。
 *
 * 调透明度：改 --activity-mask-alpha / --activity-panel-alpha 即可。
 */
.el-overlay.activity-detail-dialog-mask {
  --activity-mask-alpha: 0.38;
  --activity-panel-alpha: 0.85;

  top: 86px;
  right: 0;
  bottom: 0;
  left: 0;
  height: auto !important;
  background-color: rgba(0, 0, 0, var(--activity-mask-alpha)) !important;
  /* 仅遮罩区域虚化（从导航下缘开始，不糊导航栏） */
  backdrop-filter: blur(10px) saturate(140%);
  -webkit-backdrop-filter: blur(10px) saturate(140%);
}

.el-overlay.activity-detail-dialog-mask .el-overlay-dialog {
  /* 对话框定位层：轻微虚化背后内容 */
  backdrop-filter: blur(8px) saturate(130%);
  -webkit-backdrop-filter: blur(8px) saturate(130%);
}

.el-overlay.activity-detail-dialog-mask .el-dialog {
  background: rgba(22, 24, 30, var(--activity-panel-alpha)) !important;
  backdrop-filter: saturate(160%) blur(20px) !important;
  -webkit-backdrop-filter: saturate(160%) blur(20px) !important;
  border: 0.5px solid rgba(255, 255, 255, 0.12);
  box-shadow: 0 16px 48px rgba(0, 0, 0, 0.45);
}

html.dark .el-overlay.activity-detail-dialog-mask .el-dialog {
  margin-top: 120px !important;
  background: rgba(22, 24, 30, var(--activity-panel-alpha)) !important;
  backdrop-filter: saturate(160%) blur(20px) !important;
  -webkit-backdrop-filter: saturate(160%) blur(20px) !important;
}

.el-overlay.activity-detail-dialog-mask .el-dialog__header {
  padding: 10px 12px 0 !important;
  margin: 0 !important;
  border-bottom: none !important;
}

.el-overlay.activity-detail-dialog-mask .el-dialog__headerbtn {
  top: 10px;
  right: 10px;
  z-index: 3;
}

.el-overlay.activity-detail-dialog-mask .el-dialog__body {
  padding-top: 8px !important;
  max-height: calc(92vh - 120px);
  overflow-y: auto;
  font-size: 15px;
}

.el-overlay.activity-detail-dialog-mask .el-button {
  font-size: 15px;
}

.el-overlay.activity-detail-dialog-mask .el-textarea__inner {
  font-size: 15px;
  line-height: 1.55;
}
</style>
